<template>
  <div class="se-root absolute inset-0 flex flex-col bg-gray-100 text-charcoal-700 dark:bg-charcoal-900 dark:text-gray-100 select-none" :class="{ dark: isDark }">

    <!-- ROW 1: HEADER -->
    <header class="h-14 flex items-center justify-between px-4 shrink-0 bg-blue/90 dark:bg-heroblue">
      <div class="flex items-center gap-2">

        <!-- todo: when we do smaller screens
          <core-logo class="max-h-[22px]" tone="white" variant="circle" />
        -->
        <core-logo class="w-[120px]" tone="white" />

        <u-divider orientation="vertical" class="h-8 opacity-50 mx-2" :ui="{border: {base: 'dark:border-white/75'}}" />
        <u-dropdown :items="fileMenuItems" :popper="{ placement: 'bottom-start' }">
          <button
            class="text-white bg-blue-600/50 hover:bg-blue-600 dark:bg-black/15 dark:hover:bg-black/25 transition-colors px-3 rounded-md h-9 font-semibold">File
          </button>
          <template #file-menu-header>
            <div class="px-3 py-2 text-left">
              <div class="text-sm font-semibold text-charcoal dark:text-gray-100">{{title || 'Untitled'}}</div>
              <div class="text-xs text-gray-500 dark:text-gray-300">
                by {{currentUser?.name || 'Unknown'}} ({{surfaceSizeLabel}})
              </div>
            </div>
          </template>
        </u-dropdown>
        <u-divider orientation="vertical" class="h-8 opacity-50 mx-2" :ui="{border: {base: 'dark:border-white/75'}}" />
        <core-tooltip text="Undo" :shortcuts="['⌘/CTRL', 'Z']" :prevent="!surfaceEditorStore.canUndo" placement="bottom">
          <button
            class="text-white bg-blue-600/50 hover:bg-blue-600 transition-colors px-4 rounded-md h-9 dark:bg-black/15 dark:hover:bg-black/25"
            :class="{'opacity-40 cursor-not-allowed': !surfaceEditorStore.canUndo}"
            :disabled="!surfaceEditorStore.canUndo"
            @click="surfaceEditorStore.undo"
          >
            <u-icon name="i-ri-arrow-go-back-line" class="flex text-xl"/>
          </button>
        </core-tooltip>
        <core-tooltip text="Redo" :shortcuts="['⇧', '⌘/CTRL', 'Z']" :prevent="!surfaceEditorStore.canRedo" placement="bottom">
          <button
            class="text-white bg-blue-600/50 hover:bg-blue-700 transition-colors px-4 rounded-md h-9 dark:bg-black/15 dark:hover:bg-black/25"
            :class="{'opacity-40 cursor-not-allowed': !surfaceEditorStore.canRedo}"
            :disabled="!surfaceEditorStore.canRedo"
            @click="surfaceEditorStore.redo"
          >
            <u-icon name="i-ri-arrow-go-forward-line" class="flex text-xl"/>
          </button>
        </core-tooltip>

        <u-divider orientation="vertical" class="h-8 opacity-50 mx-2"  :ui="{border: {base: 'dark:border-white/75'}}"/>

        <core-tooltip text="All changes saved" placement="bottom">
          <button class="text-white h-9">
            <u-icon name="i-mdi-cloud-check-variant" class="flex text-2xl"/>
          </button>
        </core-tooltip>
      </div>
      <div class="flex-1 min-w-0 px-4 flex items-center justify-end" v-if="title">
        <span class="font-semibold text-base text-white ellipsis-overflow min-w-0">{{title}}</span>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <div class="flex gap-2">
          <button
            v-if="hasCustomExport && project && !reloadError"
            class="text-white bg-heroblue hover:bg-heroblue-700 dark:bg-heroblue-700 dark:hover:bg-heroblue-900 transition-colors px-4 rounded-md h-9 font-semibold flex gap-2 items-center"
            @click="exportImage" :disabled="isExporting">
            <u-icon v-if="isExporting" name="i-ri-loader-2-line" class="animate-spin"/>
            <span>{{exportActionText}}</span>
          </button>
          <button
            class="text-white bg-charcoal hover:bg-charcoal-700 dark:bg-charcoal-700 dark:hover:bg-charcoal-900 transition-colors px-4 rounded-md h-9 font-semibold flex gap-2 items-center"
            :disabled="isClosing"
            @click="closeEditor()">
            <u-icon v-if="isClosing" name="i-ri-loader-2-line" class="animate-spin"/>
            <span>{{ hasCustomExport ? 'Close' : 'Done' }}</span>
          </button>
        </div>
      </div>
    </header>

    <div v-if="error || reloadError" class="flex-center">
      <core-contextual-error-box :action="refresh"/>
    </div>

    <template v-else>
      <!-- ROW 2: LEFT COLS + RIGHT COL (DESIGN AREA + FOOTER) -->
      <div class="flex flex-1 overflow-hidden">

        <!-- LEFT COLS: TAB RAIL + PANEL -->
        <surface-editor-tabs :photos-tab-component="photosTabComponent" :photos-tab-component-props="photosTabComponentProps"/>

        <!-- RIGHT COL: DESIGN AREA (flex-1) + FOOTER -->
        <div class="flex flex-1 flex-col overflow-hidden bg-gray-200 dark:bg-charcoal-800">

          <!-- DESIGN AREA -->
          <div class="flex-1 relative">
            <surface-editor-stage ref="stageInstance" v-if="surfaces?.length" />
          </div>

          <!-- FOOTER (INSIDE ROW 2, RIGHT COL BOTTOM) -->
          <footer class="h-12 bg-white dark:bg-charcoal-900 flex items-center justify-between px-4 border-l border-gray-200 dark:border-charcoal-700">
            <div class="flex-1"><!-- room for extra controls --></div>
            <div>
              <div class="flex items-center gap-2">

                <div ref="zoomWheelTarget" class="w-48">
                  <u-range
                    :min="SURFACE_EDITOR_MIN_SCALE"
                    :max="SURFACE_EDITOR_MAX_SCALE"
                    :step="SURFACE_EDITOR_SCALE_STEP"
                    v-model="scale"
                    class="w-full"
                  />
                </div>

                <u-dropdown :items="zoomOptions">
                  <u-button color="gray" variant="ghost"><span>{{zoomPercent}}%</span></u-button>
                </u-dropdown>

                <core-tooltip text="Toggle fullscreen">
                  <u-button
                    color="gray"
                    variant="ghost"
                    :icon="isFullscreen ? 'i-ri-fullscreen-exit-line' : 'i-ri-fullscreen-line'"
                    @click="isFullscreen ? exit() : enter()"
                  />
                </core-tooltip>

                <!--<u-tooltip text="Help">
                  <u-button color="gray" variant="ghost" icon="i-ri-question-line"/>
                </u-tooltip>-->
              </div>
            </div>
          </footer>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import {provideSurfaceEditorPhotoActions} from '~/composables/surface-editor/photo-actions.js';
  import {provideSurfaceEditorTheme} from '~/composables/surface-editor/theme.js';

  const emit = defineEmits(['project-created']);
  const props = defineProps({
    projectId: String,
    /*
      config for the surface.
      width, height: dimensions
      projectType: optional project creation type
      designProjectType: template filtering/saving context
      exportType: defaults to 'jpeg'
      exportActionText: label for the save/export button, defaults to 'Save'
      handleExport: async function({ project, conf, exportSurface, exportAllSurfaces, exportedSurface })
                    called on export confirmation; if omitted, defaults to persist + close
     */
    conf: Object,
    files: Array,
    hasMoreFiles: Boolean,
    getFiles: Function,
    clearFiles: Function,
    title: String,
    modal: Object,
    photosTabComponent: {
      type: [Object, null],
      default: null
    },
    photosTabComponentProps: {
      type: Object,
      default: () => ({})
    },
    defaultTheme: {
      type: String,
      default: SURFACE_EDITOR_COLOR_MODES.light,
      validator: value => Object.values(SURFACE_EDITOR_COLOR_MODES).includes(value)
    },
    dynamicTextData: {
      type: Object,
      default: () => ({})
    }
  });

  const stageInstance = ref(null);
  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale, canRedo, canUndo, surfaces, project, activeSurface, positionGuidesEnabled} = storeToRefs(surfaceEditorStore);
  const sessionStore = useSessionStore();
  const {currentUser} = storeToRefs(sessionStore);
  const isAdmin = computed(() => currentUser.value?.can.adminDesignEditor);

  const userThemeMode = ref(null);
  const validThemeModes = Object.values(SURFACE_EDITOR_COLOR_MODES);
  const safeDefaultTheme = computed(() =>
    props.defaultTheme === SURFACE_EDITOR_COLOR_MODES.dark
      ? SURFACE_EDITOR_COLOR_MODES.dark
      : SURFACE_EDITOR_COLOR_MODES.light
  );
  const themeMode = computed(() => userThemeMode.value ?? safeDefaultTheme.value ?? SURFACE_EDITOR_COLOR_MODES.light);
  const isDark = computed(() => themeMode.value === SURFACE_EDITOR_COLOR_MODES.dark);

  function getThemeModeStorageKey() {
    return `surface-editor:themeMode:${currentUser.value?.id || 'anonymous'}`;
  }

  function loadPersistedThemeMode() {
    if (!import.meta.client) {
      userThemeMode.value = null;
      return;
    }

    const raw = localStorage.getItem(getThemeModeStorageKey());
    userThemeMode.value = validThemeModes.includes(raw) ? raw : null;
  }

  function setThemeMode(mode) {
    if (!validThemeModes.includes(mode)) {
      return;
    }

    userThemeMode.value = mode;

    if (import.meta.client) {
      localStorage.setItem(getThemeModeStorageKey(), mode);
    }
  }

  function toggleThemeMode() {
    setThemeMode(
      themeMode.value === SURFACE_EDITOR_COLOR_MODES.dark
        ? SURFACE_EDITOR_COLOR_MODES.light
        : SURFACE_EDITOR_COLOR_MODES.dark
    );
  }

  watch(() => currentUser.value?.id, loadPersistedThemeMode, {immediate: true});

  provideSurfaceEditorTheme({
    themeMode,
    isDark,
    setThemeMode,
    toggleThemeMode
  });

  watch(
    [() => props.conf, () => props.dynamicTextData],
    ([conf, dynamicTextData]) => {
      surfaceEditorStore.setConf({
        ...(conf || {}),
        dynamicTextData: dynamicTextData || {}
      });
    },
    {immediate: true, deep: true}
  );

  provideSurfaceEditorPhotoActions();

  const {isFullscreen, enter, exit} = useFullscreen();

  defineShortcuts({
    meta_z: {
      handler: () => {
        if (canUndo.value) surfaceEditorStore.undo();
      }
    },
    meta_shift_z: {
      handler: () => {
        if (canRedo.value) surfaceEditorStore.redo();
      }
    }
  });

  useZoomShortcuts({
    zoomIn: () => stageInstance.value.zoomIn(),
    zoomOut: () => stageInstance.value.zoomOut()
  });

  const zoomWheelTarget = ref(null);

  onMounted(() => {
    const el = zoomWheelTarget.value;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? +1 : -1;

      if (direction === + 1) {
        stageInstance.value?.zoomIn();
      } else {
        stageInstance.value?.zoomOut();
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });

    onUnmounted(() => {
      el.removeEventListener('wheel', onWheel);
    });
  });

  const zoomPercent = computed(() => Math.round(scale.value * 100));
  const zoomOptions = [
    [
      {label: '300%', click: () => stageInstance.value.updateScale(3)},
      {label: '200%', click: () => stageInstance.value.updateScale(2)},
      {label: '150%', click: () => stageInstance.value.updateScale(1.5)},
      {label: '100%', click: () => stageInstance.value.updateScale(1)},
      {label: '75%', click: () => stageInstance.value.updateScale(.75)},
      {label: '50%', click: () => stageInstance.value.updateScale(.5)},
      {label: '25%', click: () => stageInstance.value.updateScale(.25)},
      {label: '10%', click: () => stageInstance.value.updateScale(.1)},
    ],
    [
      {label: 'Fit', click: () => stageInstance.value.updateScale('fit')},
      {label: 'Fill', click: () => stageInstance.value.updateScale('fill')}
    ]
  ];

  const surfaceSizeLabel = computed(() => {
    const surface = activeSurface.value || surfaces.value?.[0];
    if (!surface) return '—';

    return `${surface.width} x ${surface.height}`;
  });

  const fileMenuItems = computed(() => [
    [
      {
        slot: 'file-menu-header',
        class: 'cursor-default',
        click: () => {}
      }
    ],
    [
      {
        label: positionGuidesEnabled.value ? 'Snapping/Guides: On' : 'Snapping/Guides: Off',
        icon: positionGuidesEnabled.value ? 'i-ri-ruler-2-line' : 'i-ri-ruler-line',
        click: () => surfaceEditorStore.togglePositionGuides()
      },
      {
        label: 'Dark Mode',
        icon: isDark.value ? 'i-ri-checkbox-line' : 'i-ri-checkbox-blank-line',
        click: () => toggleThemeMode()
      },
      {
        label: 'Save (all saved)',
        icon: 'i-mdi-cloud-check-variant',
        click: () => {}
      },
      {
        label: 'Download',
        icon: 'i-ri-download-2-line',
        click: () => downloadSurface()
      },
  /*    {
        label: 'Share (coming soon)',
        icon: 'i-ri-share-line',
        disabled: true
      },*/
      {
        label: 'Reset',
        icon: 'i-ri-loop-left-line',
        click: () => confirmResetSurface(),
        disabled: !surfaces.value?.length
      },
      ...(isAdmin.value ? [{
        label: 'Resize',
        icon: 'i-ri-crop-line',
        click: () => openResizeSurfaceModal(),
        disabled: !activeSurface.value
      }] : [])
    ],
    [
      {
        label: 'Suggest Improvement',
        icon: 'i-ri-lightbulb-line',
        href: 'https://docs.google.com/forms/d/e/1FAIpQLScdJyASp4w4aHxadoHQIr1_hY2Yjb9oXuWiDi61ynEmgTq7AA/viewform',
        target: '_blank',
        class: 'no-underline'
      }
    ]
  ]);

  const {exportSurface, exportAllSurfaces, isExporting} = useProjectExport();
  const hasCustomExport = computed(() => typeof props.conf?.handleExport === 'function');
  const exportActionText = computed(() => props.conf?.exportActionText || 'Save');

  async function exportCurrentSurface({surfaceIndex = 0} = {}) {
    return exportSurface({
      project: project.value,
      surfaceIndex,
      exportType: props.conf?.exportType
    });
  }

  async function exportAllProjectSurfaces() {
    return exportAllSurfaces({
      project: project.value,
      exportType: props.conf?.exportType
    });
  }

  async function runDefaultSave() {
    await closeEditor();
  }

  const isClosing = ref(false);

  function nextFrame() {
    return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  /*
    Generic pre-finalize step for any terminal editor action (apply / export / close / done /
    download). If an element is currently being edited (e.g. a text box with the caret in it),
    the live edit overlay holds changes that are only committed to the store when its editor
    component unmounts. Take the element out of edit mode first, wait for that unmount + the
    static element to re-render/re-measure, then flush a persist so the API has the final state
    before we render or leave.
  */
  async function finalizePendingEdit() {
    if (surfaceEditorStore.editedElement) {
      surfaceEditorStore.editElement({element: null});
      // let element/edit.vue unmount (its onUnmounted commits the pending change)
      // and the static element re-render / re-measure its height
      await nextTick();
      await nextFrame();
      await nextTick();
    }

    if (project.value && !reloadError.value) {
      try {
        await surfaceEditorStore.persistNow();
      } catch (e) {
        // persist failed — callers still proceed with their action
      }
    }
  }

  async function prepareEditorForClose() {
    surfaceEditorStore.clearSelection();
    surfaceEditorStore.closeContextualPanel();
    surfaceEditorStore.closeContextMenu();
    await nextTick();
  }

  async function closeEditor() {
    if (isClosing.value) return;
    isClosing.value = true;

    await finalizePendingEdit();
    await prepareEditorForClose();

    await props.modal.close();
    isClosing.value = false;
  }

  function buildExportContext() {
    return {
      project: project.value,
      conf: props.conf || {},
      exportSurface: exportCurrentSurface,
      exportAllSurfaces: exportAllProjectSurfaces
    };
  }

  async function exportImage() {
    await finalizePendingEdit();

    if (typeof props.conf?.handleExport !== 'function') {
      await runDefaultSave();
      return;
    }

    await props.conf.handleExport(buildExportContext());
  }

  function confirmResetSurface() {
    const surface = activeSurface.value;
    if (!surface) return;

    useConfirmModal({
      heading: 'Reset Design',
      message: 'This will remove all elements and reset the background to white.',
      confirmText: 'Reset',
      color: 'red',
      confirmMethod: () => {
        surfaceEditorStore.clearSelection();
        surfaceEditorStore.closeContextualPanel();
        surfaceEditorStore.closeContextMenu();
        surfaceEditorStore.resetSurface({surface, backgroundColor: '#FFFFFF'});
      }
    }).open();
  }

  function openResizeSurfaceModal() {
    const surface = activeSurface.value;
    if (!surface) return;

    useFModal({
      size: 'sm',
      component: defineAsyncComponent(() => import('~/components/surface-editor/resize-surface-modal.vue')),
      props: {
        currentWidth: surface.width,
        currentHeight: surface.height
      },
      eventHandlers: {
        resize({width, height}) {
          surfaceEditorStore.clearSelection();
          surfaceEditorStore.closeContextualPanel();
          surfaceEditorStore.closeContextMenu();
          surfaceEditorStore.resizeSurface({surface, width, height});

          const orientationTypes = surfaceEditorStore.conf?.orientationDesignProjectTypes;
          if (orientationTypes) {
            const wasPortrait = surface.height > surface.width;
            const isPortrait = height > width;
            if (wasPortrait !== isPortrait) {
              const newDesignProjectType = isPortrait ? orientationTypes.portrait : orientationTypes.landscape;
              surfaceEditorStore.setConf({...surfaceEditorStore.conf, designProjectType: newDesignProjectType});
              surfaceEditorStore.clearTemplates();
            }
          }

          this.close();
        }
      }
    }).open();
  }

  const isDownloading = ref(false);
  async function downloadSurface() {
    if (isExporting.value) return;
    isDownloading.value = true;
    await finalizePendingEdit();
    const exportedSurface = await exportCurrentSurface();
    const url = URL.createObjectURL(exportedSurface.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'editor-export.jpg';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    isDownloading.value = false;
  }

  const {error, refresh} = useAsyncData(
    `design-surface-data-${props.projectId || 'new'}`,
    async () => {
      if (props.projectId) {
        await surfaceEditorStore.getProject({id: props.projectId});

      } else {
        const project = await surfaceEditorStore.createProject();
        emit('project-created', project);
      }

      await surfaceEditorStore.getFonts();
    }
  );

  const persistErrorEvent = useEventBus('surface-editor-persist-error');
  const reloadError = ref(null);

  function resetEditorState() {
    useConfirmModal({
      id: 'surface-editor-reset-modal',
      heading: 'Error',
      message: 'We have encountered an error and need to reload your project. Apologies for the inconvenience!',
      confirmText: 'Reload',
      nonCancelable: true,
      confirmMethod: async () => {
        try {
          await surfaceEditorStore.getProject({id: surfaceEditorStore.project.id});
        } catch (e) {
          reloadError.value = e;
        }
      }
    }).open();
  }

  onMounted(() => {
    persistErrorEvent.on(resetEditorState);
  });

  onUnmounted(() => {
    persistErrorEvent.off(resetEditorState);
    surfaceEditorStore.clear();
  });

</script>

<style lang="scss" scoped>
  :deep(.cui-surface-editor-text) {
    font-feature-settings: "calt" 0, "clig" 0, "liga" 0;
    font-kerning: normal;
    text-rendering: geometricPrecision;
  }
</style>
